import { Button, cn, ResponsiveTable, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import { DuplicateSitesBanner } from './components/DuplicateSitesBanner.js';
import { ALLOWED_JOB_STATUSES, type JobStatus, type JobsListResponse } from './contractsMirror.js';
import type { JobsSearch } from './searchParams.js';
import { useJobAccess } from './useJobAccess.js';
import { useArchiveJobMutation, useRestoreJobMutation } from './useJobQuery.js';

/**
 * The jobs list (UI-SPEC archetype A — list + detail) on the locked Velocity brand. Page wrapper
 * + header (title, subtitle, "+ Add Job") + a labelled search input + the status-filter pill row
 * (the 4 ALLOWED_JOB_STATUSES with SERVER counts) + the show-archived toggle + a semantic job
 * table — or the empty state.
 *
 * The coarse endpoint (22-10) owns the status split + counts; this view renders the returned
 * payload and applies the free-text search IN-MEMORY over the already-loaded list (small dataset,
 * no extra round-trip). Each status pill is a typed `<Link>` NAVIGATION (deep-linkable). The SPA
 * never sends a tenant id and never re-aggregates (SEC-11 / CARVE-07).
 */

export interface JobsViewProps {
  data: JobsListResponse;
  search: JobsSearch;
}

export function JobsView({ data, search }: JobsViewProps) {
  const { t } = useTranslation();
  const { canCreate } = useJobAccess();
  const fmt = useMoneyFormatters();
  const archiveMutation = useArchiveJobMutation();
  const restoreMutation = useRestoreJobMutation();
  const [archiveTarget, setArchiveTarget] = useState<{ id: number; name: string } | null>(null);

  const jobs = data.jobs ?? [];
  const statusCounts = data.statusCounts;
  const total = Number(data.total ?? jobs.length);
  const showArchived = Boolean(search.show_archived);
  const activeStatus = search.status ?? null;

  const [queryText, setQueryText] = useState('');

  const filtered = useMemo(() => {
    const q = queryText.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter(
      (job) =>
        (job.jobName ?? '').toLowerCase().includes(q) ||
        (job.clientName ?? '').toLowerCase().includes(q) ||
        (job.jobCode ?? '').toLowerCase().includes(q),
    );
  }, [jobs, queryText]);

  const hasData = filtered.length > 0;
  const searching = queryText.trim().length > 0;

  // Duplicates are computed over the FULL list, not `filtered` — a search that happens to match one
  // spelling of an address would otherwise hide the very cluster the banner exists to report. It is
  // also skipped entirely on the archived view: a merged-away job is archived by design, so every
  // cluster there would be a duplicate the office has already dealt with.
  const duplicateCandidates = showArchived ? [] : jobs;

  function statusLinkSearch(status: JobStatus | null): JobsSearch {
    const next: JobsSearch = {};
    if (status) next.status = status;
    if (showArchived) next.show_archived = true;
    return next;
  }

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {showArchived ? t('jobs:archivedTitle') : t('jobs:title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">
            {showArchived ? t('jobs:archivedSubtitle') : t('jobs:subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canCreate ? (
            <Button asChild>
              <Link to="/jobs/new">{t('jobs:actions.addJob')}</Link>
            </Button>
          ) : null}
        </div>
      </header>

      <DuplicateSitesBanner jobs={duplicateCandidates} />

      <div className="mb-4">
        <label htmlFor="jobs-search" className="sr-only">
          {t('jobs:search.label')}
        </label>
        <div className="relative max-w-md">
          <Search
            className="pointer-events-none absolute top-3.5 left-3 size-4 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            id="jobs-search"
            type="search"
            value={queryText}
            onChange={(event) => setQueryText(event.target.value)}
            placeholder={t('jobs:search.placeholder')}
            className="h-11 w-full rounded-md border border-border bg-card pr-3 pl-9 text-foreground text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          />
        </div>
      </div>

      {/* Status filter pills (the 4 allowed statuses with SERVER counts) — each is a typed Link. */}
      <section
        aria-label={t('jobs:filter.regionLabel')}
        className="mb-4 flex flex-wrap items-center gap-2"
      >
        <Link
          to="/jobs"
          search={statusLinkSearch(null)}
          className={cn(
            'inline-flex h-9 items-center gap-1.5 rounded-full border px-3 font-medium text-sm transition-[color,background-color,transform] duration-[var(--dur-quick)] ease-[var(--ease-out)] active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            activeStatus === null
              ? 'border-transparent bg-secondary text-secondary-foreground'
              : 'border-border bg-card text-muted-foreground hover:bg-secondary',
          )}
        >
          {t('jobs:filter.all')}
          <span className="font-mono text-xs">{total}</span>
        </Link>
        {ALLOWED_JOB_STATUSES.map((status) => {
          const isActive = activeStatus === status;
          const count = statusCounts ? statusCounts[status] : 0;
          return (
            <Link
              key={status}
              to="/jobs"
              search={statusLinkSearch(status)}
              className={cn(
                'inline-flex h-9 items-center gap-1.5 rounded-full border px-3 font-medium text-sm transition-[color,background-color,transform] duration-[var(--dur-quick)] ease-[var(--ease-out)] active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                isActive
                  ? 'border-transparent bg-secondary text-secondary-foreground'
                  : 'border-border bg-card text-muted-foreground hover:bg-secondary',
              )}
            >
              {t(`jobs:status.${statusKey(status)}`)}
              <span className="font-mono text-xs">{count}</span>
            </Link>
          );
        })}
        <Link
          to="/jobs"
          search={
            showArchived
              ? activeStatus
                ? { status: activeStatus }
                : {}
              : activeStatus
                ? { status: activeStatus, show_archived: true }
                : { show_archived: true }
          }
          className="ml-auto inline-flex h-9 items-center rounded-md border border-border bg-card px-3 font-medium text-foreground text-sm transition-[color,background-color,transform] duration-[var(--dur-quick)] ease-[var(--ease-out)] hover:bg-secondary active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {showArchived ? t('jobs:filter.hideArchived') : t('jobs:filter.showArchived')}
        </Link>
      </section>

      {hasData ? (
        <ResponsiveTable className="rounded-md border border-border bg-card">
          <table className="w-full border-collapse text-left text-sm">
            <caption className="sr-only">{t('jobs:table.caption')}</caption>
            <thead>
              <tr className="border-border border-b text-muted-foreground text-xs uppercase tracking-wide">
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('jobs:table.job')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('jobs:table.client')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('jobs:table.status')}
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium">
                  {t('jobs:table.contract')}
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium">
                  {t('jobs:table.profit')}
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium">
                  {t('jobs:table.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((job) => {
                const profit = Number(job.financials?.profit ?? 0);
                return (
                  <tr
                    key={job.id}
                    className="border-border/60 border-b last:border-b-0 hover:bg-secondary/40"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to="/jobs/$id"
                        params={{ id: String(job.id) }}
                        className="font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                      >
                        {job.jobName || t('jobs:table.unnamed', { id: job.id })}
                      </Link>
                      {job.jobCode ? (
                        <span className="ml-2 font-mono text-muted-foreground text-xs">
                          {job.jobCode}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {job.clientName || t('jobs:table.none')}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-md border border-border bg-background px-2 py-0.5 text-muted-foreground text-xs">
                        {job.status
                          ? t(`jobs:status.${statusKey(job.status as JobStatus)}`, {
                              defaultValue: job.status,
                            })
                          : t('jobs:table.none')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-foreground">
                      {fmt.formatCurrency(Number(job.contractAmount ?? 0))}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 text-right font-mono font-semibold',
                        profit >= 0 ? 'text-success' : 'text-destructive',
                      )}
                    >
                      {job.financials ? fmt.formatCurrency(profit) : <span aria-hidden>—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {showArchived ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={restoreMutation.isPending}
                          onClick={() => restoreMutation.mutate({ jobId: job.id })}
                        >
                          {t('jobs:actions.restore')}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setArchiveTarget({
                              id: job.id,
                              name: job.jobName || t('jobs:table.unnamed', { id: job.id }),
                            })
                          }
                        >
                          {t('jobs:actions.archive')}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ResponsiveTable>
      ) : (
        <div className="rounded-md border border-border border-dashed bg-card p-12 text-center">
          <h2 className="font-display font-semibold text-foreground text-lg">
            {searching
              ? t('jobs:empty.searchHeading')
              : showArchived
                ? t('jobs:empty.archivedHeading')
                : t('jobs:empty.heading')}
          </h2>
          <p className="mt-1 text-muted-foreground text-sm">
            {searching
              ? t('jobs:empty.searchBody')
              : showArchived
                ? t('jobs:empty.archivedBody')
                : t('jobs:empty.body')}
          </p>
        </div>
      )}

      <ConfirmDialog
        open={archiveTarget !== null}
        heading={t('jobs:archive.confirmHeading')}
        body={t('jobs:archive.confirmBody', { job: archiveTarget?.name ?? '' })}
        confirmLabel={t('jobs:actions.archive')}
        cancelLabel={t('jobs:archive.cancel')}
        confirmPending={archiveMutation.isPending}
        onCancel={() => setArchiveTarget(null)}
        onConfirm={() => {
          if (!archiveTarget) return;
          archiveMutation.mutate(
            { jobId: archiveTarget.id },
            { onSuccess: () => setArchiveTarget(null) },
          );
        }}
      />
    </div>
  );
}

/** Map a status label to its i18n key segment ("On Hold" → "onHold"). */
function statusKey(status: JobStatus): string {
  switch (status) {
    case 'On Hold':
      return 'onHold';
    default:
      return status.toLowerCase();
  }
}
